import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, type JSX } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  MoreHorizontal,
  RefreshCcw,
  TrendingUp,
} from 'lucide-react';
import clsx from 'clsx';
import { z } from 'zod';
import { apiRequest, ApiError } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { useToast } from '../lib/toast';
import { useConfirm } from '../components/ui/Confirm';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { EmptyState } from '../components/ui/EmptyState';
import { GatedSection } from '../components/shared/GatedSection';
import { DetailHeader } from '../components/shared/DetailHeader';
import { KpiStrip, type KpiItem } from '../components/shared/KpiStrip';
import { formatDate, timeAgo } from '../lib/format';
import { ActivityPanel } from '../components/shared/ActivityPanel';
import {
  AxisBreakdown,
  ModerationChip,
  SubmissionModerationDrawer,
  TrendSparkline,
  FEEDBACK_AXES_ORDER,
  FEEDBACK_AXIS_LABEL,
  formatPeriodLabel,
  formatScore,
  lastNPeriodKeys,
  previousMonthPeriodKey,
  type FeedbackAdminAggregateItem,
  type FeedbackAdminAggregatesResponse,
  type FeedbackAdminListResponse,
  type FeedbackAdminSubmission,
  type FeedbackTrendResponse,
} from '../components/feedback';

// -------------------------------------------------------------------------
// Route definition + search-param validation
// -------------------------------------------------------------------------

const searchSchema = z.object({
  /**
   * Period this page is centred on (YYYY-MM). Optional; when absent the
   * page defaults to the previous calendar month (the most recent period
   * with collected feedback).
   */
  periodKey: z
    .string()
    .regex(/^\d{4}-\d{2}$/)
    .optional(),
});

type Search = z.infer<typeof searchSchema>;

export const Route = createFileRoute(
  '/_authed/hr/feedback/teachers/$teacherId',
)({
  component: TeacherAggregateDetailPage,
  validateSearch: (input: Record<string, unknown>): Search =>
    searchSchema.parse(input),
});

// -------------------------------------------------------------------------
// Page
// -------------------------------------------------------------------------

type Tab =
  | 'axes'
  | 'submissions'
  | 'per-batch'
  | 'trend'
  | 'form'
  | 'activity';

const TAB_OPTIONS: ReadonlyArray<{ value: Tab; label: string }> = [
  { value: 'axes', label: 'Axis breakdown' },
  { value: 'submissions', label: 'Submissions' },
  { value: 'per-batch', label: 'Per-batch' },
  { value: 'trend', label: 'Trend' },
  { value: 'form', label: 'Form' },
  { value: 'activity', label: 'Activity' },
];

function TeacherAggregateDetailPage(): JSX.Element {
  const { teacherId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const hasRole = useAuthStore((s) => s.hasRole);
  const canManage = hasRole('super_admin', 'academic_admin');
  const isSuperAdmin = hasRole('super_admin');
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { confirm } = useConfirm();

  // Period defaults to the previous calendar month — the same default the
  // aggregator uses when no explicit period is supplied.
  const periodKey = useMemo(
    () => search.periodKey ?? previousMonthPeriodKey(),
    [search.periodKey],
  );

  const setPeriodKey = (next: string): void => {
    void navigate({
      to: '/hr/feedback/teachers/$teacherId',
      params: { teacherId },
      search: { periodKey: next },
      replace: true,
    });
  };

  const [tab, setTab] = useState<Tab>('axes');
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [drawerSubmission, setDrawerSubmission] =
    useState<FeedbackAdminSubmission | null>(null);

  // Fetch the aggregate row for (teacherId, periodKey). The endpoint can
  // return multiple rows when batchId varies; the cross-batch canonical
  // row (batchId === null) is the headline, with per-batch rows surfaced
  // under perBatch[].
  const aggregateQuery = useQuery({
    queryKey: ['feedback', 'admin', 'aggregates', teacherId, periodKey] as const,
    queryFn: ({ signal }) =>
      apiRequest<FeedbackAdminAggregatesResponse>(
        '/api/feedback/admin/aggregates',
        {
          signal,
          query: { teacherId, periodKey },
        },
      ),
    retry: (failureCount, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 403)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const aggregateItem: FeedbackAdminAggregateItem | null = useMemo(() => {
    const items = aggregateQuery.data?.items ?? [];
    if (items.length === 0) return null;
    // Prefer the canonical (cross-batch) row; fall back to the first
    // when only synthetic placeholders exist.
    const canonical = items.find((row) => row.batchId === null);
    return canonical ?? items[0] ?? null;
  }, [aggregateQuery.data]);

  // Fetch submissions feeding this aggregate (same teacher + period).
  const submissionsQuery = useQuery({
    queryKey: [
      'feedback',
      'admin',
      'submissions',
      'by-teacher-period',
      teacherId,
      periodKey,
    ] as const,
    queryFn: ({ signal }) =>
      apiRequest<FeedbackAdminListResponse>(
        '/api/feedback/admin/submissions',
        {
          signal,
          query: { teacherId, periodKey, page: 1, pageSize: 100 },
        },
      ),
    retry: (failureCount, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 403)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  // 12-month trend for the trend tab.
  const trendQuery = useQuery({
    queryKey: ['feedback', 'teacher', 'trend', teacherId] as const,
    queryFn: ({ signal }) =>
      apiRequest<FeedbackTrendResponse>(
        `/api/feedback/teachers/${encodeURIComponent(teacherId)}/trend`,
        { signal },
      ),
    retry: (failureCount, err) => {
      if (
        err instanceof ApiError &&
        (err.status === 404 || err.status === 403)
      ) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const recomputeMutation = useMutation({
    mutationFn: () =>
      apiRequest<{
        ok: true;
        scanned: number;
        recomputed: number;
        errors: number;
      }>('/api/feedback/admin/recompute', {
        method: 'POST',
        body: { teacherId, periodKey },
      }),
    onSuccess: (res) => {
      toast(
        res.errors > 0
          ? `Recomputed with ${res.errors} error${res.errors === 1 ? '' : 's'}.`
          : 'Aggregate recomputed.',
        { variant: res.errors > 0 ? 'info' : 'success' },
      );
      void queryClient.invalidateQueries({
        queryKey: ['feedback', 'admin', 'aggregates', teacherId, periodKey],
      });
      void queryClient.invalidateQueries({
        queryKey: ['feedback', 'admin', 'submissions'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['feedback', 'teacher', 'trend', teacherId],
      });
    },
    onError: (err: unknown) =>
      toast(
        err instanceof ApiError ? err.message : 'Could not recompute.',
        { variant: 'error' },
      ),
  });

  const handleRecompute = async (): Promise<void> => {
    setOverflowOpen(false);
    const ok = await confirm({
      title: 'Recompute this teacher?',
      description: `Rebuilds the aggregate for ${formatPeriodLabel(periodKey)} using current submissions. Withdrawn and hidden rows are excluded.`,
      confirmLabel: 'Recompute',
    });
    if (!ok) return;
    recomputeMutation.mutate();
  };

  const teacherName = useMemo(() => {
    const fromAggregate = aggregateItem?.teacherName;
    if (fromAggregate) return fromAggregate;
    const fromSubmission = submissionsQuery.data?.items.find(
      (row) => row.teacherId === teacherId,
    );
    return fromSubmission?.teacherName ?? 'Teacher';
  }, [aggregateItem, submissionsQuery.data, teacherId]);

  const responsesCount =
    aggregateItem?.responsesCount ??
    submissionsQuery.data?.items.filter(
      (row) =>
        !row.withdrawnAt && row.moderationStatus !== 'hidden',
    ).length ??
    0;

  // Composite performance score linkout uses the existing teacher
  // performance leaderboard (the per-teacher detail page is not yet built);
  // the KPI shows the feedback-derived overall pending that integration.
  const kpiItems = useMemo<KpiItem[]>(() => {
    const overallX100 = aggregateItem?.overallAvgX100 ?? null;
    const weightedX100 = aggregateItem?.weightedScoreX100 ?? null;
    const trendBand = deriveTrendBand(trendQuery.data?.items, periodKey);
    return [
      {
        label: 'Overall',
        value:
          overallX100 !== null
            ? formatScore(overallX100)
            : aggregateQuery.isLoading
              ? '…'
              : '—',
        sub: formatPeriodLabel(periodKey),
      },
      {
        label: 'Responses',
        value: responsesCount,
        sub: 'this period',
      },
      {
        label: 'Weighted',
        value:
          weightedX100 !== null
            ? formatScore(weightedX100)
            : '—',
        sub: 'period score',
      },
      {
        label: 'Trend',
        value: trendBand.label,
        sub: trendBand.detail,
      },
      {
        label: 'Composite',
        value:
          overallX100 !== null ? formatScore(overallX100) : '—',
        sub: 'performance link',
        href: '/hr/performance',
      },
    ];
  }, [
    aggregateItem,
    aggregateQuery.isLoading,
    periodKey,
    responsesCount,
    trendQuery.data,
  ]);

  return (
    <div className="px-4 sm:px-6 py-6 max-w-screen-2xl mx-auto w-full">
      <GatedSection
        error={aggregateQuery.error}
        title="Teacher aggregate"
        onRetry={() => aggregateQuery.refetch()}
      >
        {aggregateQuery.isLoading && !aggregateItem ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-16 w-full" />
            <Skeleton className="h-64 w-full" />
          </div>
        ) : (
          <div className="space-y-6">
            <DetailHeader
              title={teacherName}
              sublabel={`Aggregate for ${formatPeriodLabel(periodKey)}`}
              backTo="/hr/feedback"
              actions={
                <>
                  <Link
                    to="/people/teachers/$teacherId"
                    params={{ teacherId }}
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 text-xs font-medium text-[color:var(--color-text)] hover:border-gold-500"
                  >
                    <ExternalLink size={12} aria-hidden="true" />
                    Teacher profile
                  </Link>
                  <Link
                    to="/hr/performance"
                    className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 text-xs font-medium text-[color:var(--color-text)] hover:border-gold-500"
                  >
                    <TrendingUp size={12} aria-hidden="true" />
                    Performance
                  </Link>
                  {canManage ? (
                    <OverflowMenu
                      open={overflowOpen}
                      onOpenChange={setOverflowOpen}
                      items={[
                        ...(isSuperAdmin
                          ? [
                              {
                                label: 'Recompute this teacher',
                                icon: (
                                  <RefreshCcw
                                    size={14}
                                    aria-hidden="true"
                                  />
                                ),
                                onSelect: () => {
                                  void handleRecompute();
                                },
                                disabled: recomputeMutation.isPending,
                              },
                            ]
                          : []),
                        {
                          label: 'Notify parents (performance)',
                          icon: (
                            <ExternalLink
                              size={14}
                              aria-hidden="true"
                            />
                          ),
                          onSelect: () => {
                            setOverflowOpen(false);
                            void navigate({ to: '/hr/performance' });
                          },
                        },
                      ]}
                    />
                  ) : null}
                </>
              }
            />

            <PeriodSwitcher
              periodKey={periodKey}
              onChange={setPeriodKey}
            />

            <KpiStrip items={kpiItems} />

            <div
              role="tablist"
              aria-label="Aggregate sections"
              className="flex flex-wrap gap-1 border-b border-[color:var(--color-border)]"
            >
              {TAB_OPTIONS.map((opt) => {
                const active = tab === opt.value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="tab"
                    aria-selected={active}
                    onClick={() => setTab(opt.value)}
                    className={clsx(
                      'inline-flex items-center gap-1.5 rounded-t-md px-3 py-2 text-sm font-medium transition-colors',
                      'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-500',
                      active
                        ? 'border-b-2 border-gold-500 text-[color:var(--color-text)]'
                        : 'text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]',
                    )}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>

            {tab === 'axes' ? (
              <AxesTab item={aggregateItem} />
            ) : null}
            {tab === 'submissions' ? (
              <SubmissionsTab
                query={submissionsQuery}
                onModerate={(row) => setDrawerSubmission(row)}
              />
            ) : null}
            {tab === 'per-batch' ? (
              <PerBatchTab item={aggregateItem} />
            ) : null}
            {tab === 'trend' ? (
              <TrendTab query={trendQuery} />
            ) : null}
            {tab === 'form' ? (
              <FormTab item={aggregateItem} />
            ) : null}
            {tab === 'activity' ? (
              <ActivityPanel
                targetType="feedback_aggregate"
                targetId={aggregateItem?.id ?? `synthetic::${teacherId}::${periodKey}`}
                title="Activity"
                emptyCopy="No moderation or recompute events for this aggregate yet."
              />
            ) : null}
          </div>
        )}
      </GatedSection>

      <SubmissionModerationDrawer
        submission={drawerSubmission}
        canManage={canManage}
        onClose={() => setDrawerSubmission(null)}
        onModerated={() => {
          setDrawerSubmission(null);
          void queryClient.invalidateQueries({
            queryKey: [
              'feedback',
              'admin',
              'submissions',
              'by-teacher-period',
              teacherId,
              periodKey,
            ],
          });
          void queryClient.invalidateQueries({
            queryKey: ['feedback', 'admin', 'aggregates'],
          });
        }}
      />
    </div>
  );
}

// -------------------------------------------------------------------------
// Period switcher
// -------------------------------------------------------------------------

interface PeriodSwitcherProps {
  periodKey: string;
  onChange: (next: string) => void;
}

function PeriodSwitcher({
  periodKey,
  onChange,
}: PeriodSwitcherProps): JSX.Element {
  // The aggregator anchors on `previousMonthPeriodKey()` (last calendar
  // month). We treat that as "current" — the navigator's "Current" button
  // resets to that anchor regardless of where the user navigated.
  const recent = useMemo(() => lastNPeriodKeys(12), []);
  const idx = recent.indexOf(periodKey);

  const prevKey = idx >= 0 && idx + 1 < recent.length ? recent[idx + 1] : null;
  const nextKey = idx > 0 ? recent[idx - 1] : null;
  const currentKey = previousMonthPeriodKey();

  return (
    <div
      role="group"
      aria-label="Period switcher"
      className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-4 py-3"
    >
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
          Period
        </p>
        <p className="mt-0.5 font-display text-base font-semibold">
          {formatPeriodLabel(periodKey)}
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Button
          variant="secondary"
          size="sm"
          disabled={!prevKey}
          onClick={() => prevKey && onChange(prevKey)}
          icon={<ChevronLeft size={14} aria-hidden="true" />}
        >
          Prev
        </Button>
        <Button
          variant="ghost"
          size="sm"
          disabled={periodKey === currentKey}
          onClick={() => onChange(currentKey)}
        >
          Current
        </Button>
        <Button
          variant="secondary"
          size="sm"
          disabled={!nextKey}
          onClick={() => nextKey && onChange(nextKey)}
        >
          Next
          <ChevronRight size={14} aria-hidden="true" />
        </Button>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------------
// Tabs
// -------------------------------------------------------------------------

function AxesTab({
  item,
}: {
  item: FeedbackAdminAggregateItem | null;
}): JSX.Element {
  if (!item || item.responsesCount === 0) {
    return (
      <EmptyState
        title="No aggregate row for this period"
        description="The aggregator hasn't produced a row for this teacher and period — either there are no qualifying responses yet, or the next recompute will materialise it."
      />
    );
  }
  return (
    <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
      <header className="mb-4">
        <h3 className="text-base font-semibold">Axis breakdown</h3>
        <p className="text-xs text-[color:var(--color-text-muted)]">
          Cross-batch averages computed from {item.responsesCount}{' '}
          contributing response{item.responsesCount === 1 ? '' : 's'}.
        </p>
      </header>
      <AxisBreakdown aggregate={item} />
      <p className="mt-4 text-[11px] text-[color:var(--color-text-muted)]">
        Last recomputed{' '}
        <time
          dateTime={item.lastRecomputedAt}
          title={formatDate(item.lastRecomputedAt, { withTime: true })}
        >
          {timeAgo(item.lastRecomputedAt)}
        </time>
        {item.lastSubmittedAt ? (
          <>
            {' · last submission '}
            <time
              dateTime={item.lastSubmittedAt}
              title={formatDate(item.lastSubmittedAt, { withTime: true })}
            >
              {timeAgo(item.lastSubmittedAt)}
            </time>
          </>
        ) : null}
      </p>
    </section>
  );
}

interface SubmissionsTabProps {
  query: ReturnType<typeof useQuery<FeedbackAdminListResponse, unknown>>;
  onModerate: (row: FeedbackAdminSubmission) => void;
}

function SubmissionsTab({
  query,
  onModerate,
}: SubmissionsTabProps): JSX.Element {
  const navigate = useNavigate();

  if (query.isLoading && !query.data) {
    return <Skeleton className="h-64 w-full" />;
  }

  const items = query.data?.items ?? [];
  if (items.length === 0) {
    return (
      <EmptyState
        title="No submissions for this teacher and period"
        description="The submission list is filtered to (teacher, period). Try a neighbouring period from the switcher above."
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)]">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[color:var(--color-surface)]">
            <tr>
              <Th>Submitted</Th>
              <Th>Child</Th>
              <Th>Batch</Th>
              <Th align="right">Overall</Th>
              <Th>Moderation</Th>
              <Th>Comment</Th>
              <Th align="right">
                <span className="sr-only">Actions</span>
              </Th>
            </tr>
          </thead>
          <tbody>
            {items.map((row) => (
              <tr
                key={row.id}
                onClick={() =>
                  navigate({
                    to: '/hr/feedback/submissions/$submissionId',
                    params: { submissionId: row.id },
                  })
                }
                className="cursor-pointer border-t border-[color:var(--color-border)] transition-colors hover:bg-[color:var(--color-surface)]"
              >
                <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)] whitespace-nowrap">
                  {formatDate(row.submittedAt, { withTime: true })}
                </td>
                <td className="px-4 py-2 align-middle">
                  <p className="font-medium">{row.studentName ?? '—'}</p>
                </td>
                <td className="px-4 py-2 align-middle text-xs text-[color:var(--color-text-muted)]">
                  {row.batchName ?? '—'}
                </td>
                <td className="px-4 py-2 align-middle text-right tabular-nums font-semibold">
                  {row.responses?.overall ?? '—'}
                  <span className="ml-0.5 text-[10px] text-[color:var(--color-text-muted)]">
                    /5
                  </span>
                </td>
                <td className="px-4 py-2 align-middle">
                  <ModerationChip status={row.moderationStatus} />
                </td>
                <td className="px-4 py-2 align-middle">
                  <p className="line-clamp-1 max-w-[28ch] text-xs text-[color:var(--color-text-muted)]">
                    {row.comment ?? '—'}
                  </p>
                </td>
                <td className="px-4 py-2 align-middle text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onModerate(row);
                    }}
                  >
                    Moderate
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function PerBatchTab({
  item,
}: {
  item: FeedbackAdminAggregateItem | null;
}): JSX.Element {
  if (!item || item.perBatch.length === 0) {
    return (
      <EmptyState
        title="No per-batch breakdown yet"
        description="Per-batch rows are produced when a teacher receives responses against multiple batches in the same period. The aggregator will surface them here automatically once they exist."
      />
    );
  }
  return (
    <section className="grid gap-3 lg:grid-cols-2">
      {item.perBatch.map((row) => (
        <article
          key={row.id}
          className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5"
        >
          <header className="flex flex-wrap items-baseline justify-between gap-2">
            <div className="min-w-0">
              <p className="font-display text-base font-semibold">
                {row.batchName ?? 'Batch'}
              </p>
              <p className="text-xs text-[color:var(--color-text-muted)]">
                {row.responsesCount} response
                {row.responsesCount === 1 ? '' : 's'}
              </p>
            </div>
            <span className="font-display text-xl font-bold tabular-nums">
              {formatScore(row.overallAvgX100)}
              <span className="ml-1 text-xs font-normal text-[color:var(--color-text-muted)]">
                / 5
              </span>
            </span>
          </header>
          <div className="mt-4">
            <AxisBreakdown aggregate={row} compact />
          </div>
        </article>
      ))}
    </section>
  );
}

function TrendTab({
  query,
}: {
  query: ReturnType<typeof useQuery<FeedbackTrendResponse, unknown>>;
}): JSX.Element {
  if (query.isLoading && !query.data) {
    return <Skeleton className="h-48 w-full" />;
  }
  const items = query.data?.items ?? [];
  if (items.length === 0) {
    return (
      <EmptyState
        title="No trend yet"
        description="The trend endpoint returns the last six periods once at least one aggregate row exists."
      />
    );
  }
  return (
    <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
      <header className="mb-4">
        <h3 className="text-base font-semibold">Last 6 periods</h3>
        <p className="text-xs text-[color:var(--color-text-muted)]">
          Overall score with gaps shown as small rings (insufficient
          responses).
        </p>
      </header>
      <TrendSparkline items={items} />
    </section>
  );
}

function FormTab({
  item,
}: {
  item: FeedbackAdminAggregateItem | null;
}): JSX.Element {
  if (!item) {
    return (
      <EmptyState
        title="No form linked yet"
        description="The form-level card appears once the aggregate row exists."
      />
    );
  }
  return (
    <section className="rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]">
            Form
          </p>
          <p className="mt-0.5 font-display text-base font-semibold">
            {item.formId}
          </p>
          <p className="text-xs text-[color:var(--color-text-muted)]">
            Aggregator output for {formatPeriodLabel(item.periodKey)}
          </p>
        </div>
        <Link
          to="/hr/feedback/forms/$formId"
          params={{ formId: item.formId }}
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] px-3 text-xs font-medium text-[color:var(--color-text)] hover:border-gold-500"
        >
          <ExternalLink size={12} aria-hidden="true" />
          Open form
        </Link>
      </header>
      <dl className="mt-4 grid gap-3 sm:grid-cols-2">
        <Stat label="Cross-batch overall" value={formatScore(item.overallAvgX100)} />
        <Stat label="Weighted score" value={formatScore(item.weightedScoreX100)} />
        {FEEDBACK_AXES_ORDER.filter((a) => a !== 'overall').map((axis) => {
          const x100 = {
            clarity: item.clarityAvgX100,
            patience: item.patienceAvgX100,
            communication: item.communicationAvgX100,
            progress: item.progressAvgX100,
            overall: item.overallAvgX100,
          }[axis];
          return (
            <Stat
              key={axis}
              label={FEEDBACK_AXIS_LABEL[axis]}
              value={formatScore(x100)}
            />
          );
        })}
      </dl>
    </section>
  );
}

// -------------------------------------------------------------------------
// Helpers
// -------------------------------------------------------------------------

interface TrendBand {
  label: string;
  detail: string;
}

/**
 * Derive a coarse "rising / steady / falling" band from the 6-period
 * trend. We compare the current period's overall to the median of the
 * previous five periods. The threshold (0.2 of a point) keeps small
 * sample noise from flipping the chip every recompute.
 */
const deriveTrendBand = (
  trend: FeedbackTrendResponse['items'] | undefined,
  periodKey: string,
): TrendBand => {
  if (!trend || trend.length === 0) {
    return { label: '—', detail: 'no trend yet' };
  }
  // Sort oldest-first for consistent indexing.
  const sorted = [...trend].sort((a, b) =>
    a.periodKey.localeCompare(b.periodKey),
  );
  const current = sorted.find((p) => p.periodKey === periodKey);
  if (!current || current.overallAvgX100 === null) {
    return { label: '—', detail: 'insufficient data' };
  }
  const others = sorted
    .filter(
      (p) =>
        p.periodKey !== periodKey &&
        p.overallAvgX100 !== null,
    )
    .map((p) => p.overallAvgX100 as number);
  if (others.length === 0) {
    return { label: 'New', detail: 'first scored period' };
  }
  const median = computeMedian(others);
  const delta = current.overallAvgX100 - median;
  const deltaScore = delta / 100;
  if (deltaScore >= 0.2) {
    return { label: 'Rising', detail: `+${deltaScore.toFixed(1)} vs median` };
  }
  if (deltaScore <= -0.2) {
    return { label: 'Falling', detail: `${deltaScore.toFixed(1)} vs median` };
  }
  return { label: 'Steady', detail: 'within ±0.2 of median' };
};

const computeMedian = (values: number[]): number => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 0) {
    return ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
  }
  return sorted[mid] ?? 0;
};

// -------------------------------------------------------------------------
// Primitives
// -------------------------------------------------------------------------

function Th({
  children,
  align = 'left',
}: {
  children: React.ReactNode;
  align?: 'left' | 'right';
}): JSX.Element {
  return (
    <th
      scope="col"
      className={clsx(
        'px-4 py-2 text-xs font-semibold uppercase tracking-wide text-[color:var(--color-text-muted)]',
        align === 'right' ? 'text-right' : 'text-left',
      )}
    >
      {children}
    </th>
  );
}

function Stat({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-surface)] px-3 py-2">
      <p className="text-[10px] uppercase tracking-wide text-[color:var(--color-text-muted)]">
        {label}
      </p>
      <p className="mt-0.5 font-display text-base font-semibold tabular-nums">
        {value}
      </p>
    </div>
  );
}

interface OverflowItem {
  label: string;
  icon?: JSX.Element;
  onSelect: () => void;
  disabled?: boolean;
  danger?: boolean;
}

interface OverflowMenuProps {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  items: OverflowItem[];
}

function OverflowMenu({
  open,
  onOpenChange,
  items,
}: OverflowMenuProps): JSX.Element | null {
  if (items.length === 0) return null;
  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="sm"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => onOpenChange(!open)}
        icon={<MoreHorizontal size={14} aria-hidden="true" />}
      >
        More
      </Button>
      {open ? (
        <>
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={() => onOpenChange(false)}
            className="fixed inset-0 z-10 cursor-default"
          />
          <ul
            role="menu"
            className="absolute right-0 z-20 mt-1 w-64 overflow-hidden rounded-md border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] shadow-lg"
          >
            {items.map((item) => (
              <li key={item.label} role="none">
                <button
                  type="button"
                  role="menuitem"
                  disabled={item.disabled}
                  onClick={item.onSelect}
                  className={clsx(
                    'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                    'hover:bg-[color:var(--color-surface)]',
                    'disabled:cursor-not-allowed disabled:opacity-50',
                    item.danger && 'text-[color:var(--color-danger)]',
                  )}
                >
                  {item.icon}
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </>
      ) : null}
    </div>
  );
}
